import { API_BASE_URL } from '../config/api';

export interface Meal {
  id: string;
  clientId: string;
  clientName: string;
  mealDate: string;
  mealType: string;
  foodName: string;
  quantity?: number;
  unit?: string;
  calories: number;
  protein: number;
  carbohydrates: number;
  fat: number;
  fiber: number;
  notes?: string;
  photoUrl?: string;
  createdAt: string;
  updatedAt?: string;
}

export interface CreateMealRequest {
  clientId: string;
  clientName: string;
  mealDate: string;
  mealType: string;
  foodName: string;
  quantity?: number;
  unit?: string;
  calories: number;
  protein: number;
  carbohydrates: number;
  fat: number;
  fiber: number;
  notes?: string;
  photoUrl?: string;
}

export interface UpdateMealRequest {
  mealType?: string;
  foodName?: string;
  quantity?: number;
  unit?: string;
  calories?: number;
  protein?: number;
  carbohydrates?: number;
  fat?: number;
  fiber?: number;
  notes?: string;
  photoUrl?: string;
}

export interface DailyNutritionSummary {
  date: string;
  totalCalories: number;
  totalProtein: number;
  totalCarbohydrates: number;
  totalFat: number;
  totalFiber: number;
  mealCount: number;
  meals: Meal[];
}

export interface CreateMealResponse {
  message: string;
  meal: Meal;
}

export interface UpdateMealResponse {
  message: string;
  meal: Meal;
}

export interface DeleteMealResponse {
  message: string;
}

export const mealApi = {
  async getAllMeals(): Promise<Meal[]> {
    const response = await fetch(`${API_BASE_URL}/meals`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  },

  async getMealById(id: string): Promise<Meal> {
    const response = await fetch(`${API_BASE_URL}/meals/${id}`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  },

  async getMealsByClient(clientId: string): Promise<Meal[]> {
    const response = await fetch(`${API_BASE_URL}/meals/client/${clientId}`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  },

  async getMealsByDate(clientId: string, date: Date): Promise<Meal[]> {
    const dateStr = date.toISOString().split('T')[0];
    const response = await fetch(
      `${API_BASE_URL}/meals/client/${clientId}/date/${dateStr}`
    );
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  },

  async getDailyNutritionSummary(
    clientId: string,
    date: Date
  ): Promise<DailyNutritionSummary> {
    const dateStr = date.toISOString().split('T')[0];
    const response = await fetch(
      `${API_BASE_URL}/meals/client/${clientId}/daily/${dateStr}`
    );
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  },

  async getWeeklyNutritionSummary(
    clientId: string,
    startDate: Date
  ): Promise<DailyNutritionSummary[]> {
    const dateStr = startDate.toISOString().split('T')[0];
    const response = await fetch(
      `${API_BASE_URL}/meals/client/${clientId}/weekly/${dateStr}`
    );
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  },

  async createMeal(mealData: CreateMealRequest): Promise<CreateMealResponse> {
    const response = await fetch(`${API_BASE_URL}/meals`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(mealData),
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  },

  async updateMeal(
    id: string,
    mealData: UpdateMealRequest
  ): Promise<UpdateMealResponse> {
    const response = await fetch(`${API_BASE_URL}/meals/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(mealData),
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  },

  async deleteMeal(id: string): Promise<DeleteMealResponse> {
    const response = await fetch(`${API_BASE_URL}/meals/${id}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  },
};
